import { type JSX, useEffect, useState } from 'react';

import { anlegen } from '../api/auftraege';
import { zeile as kundenzeile, type Kundenzeile } from '../api/kunden';
import { ApiFehler } from '../api/fehler';
import { Abschnitt, Feldraster } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Meldung } from '../komponenten/Meldung';
import { Kundenfeld, Kundensuchfenster } from '../suche/Kundenwahl';

/** Der heutige Tag als ISO-Datum, ohne Zeitzonensprung. */
function heute(): string {
  const j = new Date();
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

/**
 * Die Neuanlage eines Auftrags (AUF-01, AUF-02, AUF-07).
 *
 * VIER FELDER UND KEINES MEHR: Kunde, Betreff, Bestellreferenz, Herkunft.
 * Zahlungsbedingung und Fakturierungszeitpunkt kommen aus der Kundenkondition
 * (AUF-02, AUF-21) und stehen danach in der Akte — sie hier abzufragen hiesse,
 * eine Antwort zu verlangen, die das System schon kennt.
 *
 * DIE HERKUNFT IST EIN PFLICHTFELD MIT VORGABE. AUF-07 zählt fünf Wege auf,
 * und welcher es war, lässt sich später nicht mehr feststellen. „Aus Angebot"
 * steht NICHT zur Wahl: Diesen Weg gibt es, aber er führt über die
 * Angebotsakte und trägt dort die Belegkette mit (AUF-06). Ihn hier
 * anzubieten hieße, eine Herkunft behaupten zu können, für die es kein
 * Angebot gibt.
 */
export function AuftragAnlegen({
  anfangsKunde,
  beiAngelegt,
  beiAbbruch,
}: {
  readonly anfangsKunde?: string;
  readonly beiAngelegt: (id: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  // M-07: der Kunde über das Suchfenster, wie beim Angebot (Auftrag CRM-172).
  const [waehlt, setzeWaehlt] = useState(false);
  const [kunde, setzeKunde] = useState<Kundenzeile | undefined>(undefined);
  const [betreff, setzeBetreff] = useState('');
  const [referenz, setzeReferenz] = useState('');
  const [herkunft, setzeHerkunft] = useState<'telefon' | 'innendienst'>('innendienst');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  useEffect(() => {
    if (anfangsKunde === undefined || anfangsKunde === '') return;
    // Der Kunde aus der Akte, über seine Kennung und nicht über die Liste.
    void kundenzeile(anfangsKunde)
      .then(setzeKunde)
      .catch(() => undefined);
  }, [anfangsKunde]);

  async function absenden(): Promise<void> {
    if (kunde === undefined || betreff === '') return;
    setzeLaeuft(true);
    try {
      const neu = await anlegen({
        kunde: kunde.id,
        betreff,
        belegdatum: heute(),
        bestellreferenz: referenz === '' ? null : referenz,
        herkunft,
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <div className="maske maske--weit erfassung">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {waehlt && (
        <Kundensuchfenster
          beiWahl={setzeKunde}
          beiFehler={setzeFehler}
          beiSchliessen={() => setzeWaehlt(false)}
        />
      )}

      {/*
        ÄA-41, Seitentyp Erfassung: „Neuer Auftrag" steht im Seitenkopf, die
        Karte trägt zwei Abschnitte. „Anlegen" steht oben rechts und am Ende.
        Das Suchfenster steht außerhalb der Karte.
      */}
      <Erfassung
        beschriftung="Neuer Auftrag"
        beiAbsenden={() => void absenden()}
        absenden={{
          text: laeuft ? 'Wird gespeichert …' : 'Anlegen',
          gesperrt: kunde === undefined || betreff === '' || laeuft,
        }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
      >
        {/* AUF-01 */}
        <Abschnitt titel="Auftrag" zusatz="Nummer wird beim Speichern vergeben">
          <Feldraster>
            <Kundenfeld
              kennung="auftrag-kunde"
              kunde={kunde}
              offen={waehlt}
              beiSuchen={() => setzeWaehlt(true)}
            >
              {/* AUF-02, AUF-21 */}
              <p className="feld__hilfe">
                Zahlungsziel, Skonto und Fakturierungszeitpunkt werden aus der Kundenkondition
                übernommen und im Auftrag eingefroren — eine spätere Änderung der
                Kondition bewegt diesen Auftrag nicht mehr.
              </p>
            </Kundenfeld>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="auftrag-betreff">
                Betreff
              </label>
              <input
                id="auftrag-betreff"
                type="text"
                value={betreff}
                onChange={(e) => setzeBetreff(e.target.value)}
                placeholder="Fahrsicherheitstraining für Ihre Außendienstmitarbeiter"
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="auftrag-referenz">
                Bestellreferenz des Kunden
              </label>
              <input
                id="auftrag-referenz"
                type="text"
                value={referenz}
                onChange={(e) => setzeReferenz(e.target.value)}
                placeholder="Bestellnummer, Kostenstelle"
              />
              <p className="feld__hilfe">
                Erscheint auf der Rechnung. Viele Firmenkunden zahlen ohne sie nicht.
              </p>
            </div>
          </Feldraster>
        </Abschnitt>

        {/* AUF-07 */}
        <Abschnitt titel="Herkunft">
          <fieldset className="feld">
            <legend className="feld__beschriftung">Wie ist der Auftrag entstanden?</legend>
            <label className="liste__schalter">
              <input
                type="radio"
                name="auftrag-herkunft"
                checked={herkunft === 'innendienst'}
                onChange={() => setzeHerkunft('innendienst')}
              />
              im Innendienst erfasst
            </label>
            <label className="liste__schalter">
              <input
                type="radio"
                name="auftrag-herkunft"
                checked={herkunft === 'telefon'}
                onChange={() => setzeHerkunft('telefon')}
              />
              telefonisch gebucht
            </label>
            <p className="feld__hilfe">
              Das Lastenheft zählt fünf Wege auf; welcher es war, lässt sich später nicht mehr
              feststellen.
              Ein Auftrag aus einem angenommenen Angebot entsteht in der Angebotsakte — nur dort
              entsteht auch die Belegkette.
            </p>
          </fieldset>
        </Abschnitt>
      </Erfassung>
    </div>
  );
}
